"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { DragEvent } from "react";
import { AlertCircle, Database, FileIcon, Loader2, RefreshCw, Trash2, Upload } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { CreateDataSourceDialog } from "@/components/documents";
import { AccessDeniedCard } from "@/components/shared/access-denied-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useProtectedQuery } from "@/hooks/use-protected-query";
import { cn } from "@/lib/utils";
import BackgroundUploadService, {
  UploadError,
  UploadTask,
} from "@/lib/background-uploader";
import { fetchData } from "@/utils/api-handler";
import {
  ACCEPTED_DOCUMENT_FILE_TYPES,
  formatDocumentFileSize,
  getDefaultDocumentGroupId,
  getDocumentFileAcceptValue,
  getDocumentFileTypeLabel,
  getDocumentFileValidationError,
  MAX_DOCUMENT_UPLOAD_FILES,
  SUPPORTED_DOCUMENT_FORMAT_LABELS,
} from "@/utils/document-upload";

interface AgentKnowledgeBaseUploadProps {
  onCancel: () => void;
  allowedDatasourceIds?: string[];
  initialDatasourceId?: string;
}

interface DataGroup {
  id: string;
  name: string;
  description: string;
  is_default?: boolean;
}

interface DataSource {
  id: string;
  name: string;
  created_at: string;
  groups: DataGroup[];
}

interface DataSourceWithGroupsResponse {
  datasources: DataSource[];
}

interface SelectedFile {
  error?: string;
  file: File;
  id: string;
}

function FormatSupportChips() {
  return (
    <div className="flex flex-wrap gap-1.5">
      {SUPPORTED_DOCUMENT_FORMAT_LABELS.map((format) => (
        <Badge
          key={format}
          variant="outline"
          className="rounded-md border-border/60 bg-background px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
        >
          {format}
        </Badge>
      ))}
    </div>
  );
}

export function AgentKnowledgeBaseUpload({
  onCancel,
  allowedDatasourceIds,
  initialDatasourceId,
}: AgentKnowledgeBaseUploadProps) {
  const [selectedDataSource, setSelectedDataSource] = useState(initialDatasourceId ?? "");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadTasks, setUploadTasks] = useState<UploadTask[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);
  const scheduledRemovals = useRef<Set<string>>(new Set());
  const queryClient = useQueryClient();

  const restrictToAttached =
    Boolean(allowedDatasourceIds && allowedDatasourceIds.length > 0);

  const {
    data: datasourcesData,
    hasPermission,
    isLoading: isLoadingDataSources,
  } = useProtectedQuery<DataSourceWithGroupsResponse>({
    queryKey: ["datasources-with-groups"],
    queryFn: async () => fetchData("/api/documents/datasources/all"),
    requiredPermissions: ["LIST_DATA_SOURCES"],
  });

  const availableDatasources = restrictToAttached
    ? (datasourcesData?.datasources ?? []).filter((ds) =>
        allowedDatasourceIds?.includes(ds.id)
      )
    : datasourcesData?.datasources ?? [];

  const selectedDataSourceName = availableDatasources.find(
    (ds) => ds.id === selectedDataSource
  )?.name;

  const validFiles = selectedFiles.filter((selectedFile) => !selectedFile.error);
  const invalidFiles = selectedFiles.filter(
    (selectedFile) => selectedFile.error
  );
  const canUpload =
    validFiles.length > 0 && selectedDataSource && selectedGroup && !isUploading;

  useEffect(() => {
    if (!selectedDataSource) {
      setSelectedGroup("");
      return;
    }

    const groups =
      availableDatasources.find((ds) => ds.id === selectedDataSource)?.groups ?? [];
    const defaultGroupId = getDefaultDocumentGroupId(groups);
    setSelectedGroup(defaultGroupId ?? "");
  }, [selectedDataSource, availableDatasources]);

  useEffect(() => {
    if (isLoadingDataSources || !selectedDataSource) {
      return;
    }

    const isCurrentValid = availableDatasources.some(
      (ds) => ds.id === selectedDataSource
    );

    if (!isCurrentValid) {
      setSelectedDataSource("");
    }
  }, [availableDatasources, isLoadingDataSources, selectedDataSource]);

  useEffect(() => {
    const uploadService = BackgroundUploadService.getInstance();

    uploadService
      .getAllTasks()
      .filter((task) => task.status === "failed")
      .forEach((task) => uploadService.removeTask(task.id));

    const unsubscribe = uploadService.subscribe((tasks) => {
      setUploadTasks(tasks);

      tasks.forEach((task) => {
        if (task.status === "completed" && !scheduledRemovals.current.has(task.id)) {
          scheduledRemovals.current.add(task.id);
          setTimeout(() => {
            uploadService.removeTask(task.id);
            scheduledRemovals.current.delete(task.id);
          }, 5000);
        }
      });
    });

    return () => {
      uploadService
        .getAllTasks()
        .filter((task) => task.status === "failed")
        .forEach((task) => uploadService.removeTask(task.id));
      unsubscribe();
      scheduledRemovals.current.clear();
    };
  }, []);

  const handleFileSelect = useCallback(
    (files: FileList | null) => {
      if (!files) {
        return;
      }

      if (!selectedDataSource) {
        toast.error("Please select a data source first");
        return;
      }

      const existingFileNames = new Set(
        selectedFiles.map((selectedFile) => selectedFile.file.name)
      );
      const nextFiles: SelectedFile[] = [];

      Array.from(files).forEach((file) => {
        if (selectedFiles.length + nextFiles.length >= MAX_DOCUMENT_UPLOAD_FILES) {
          toast.warning(
            `Maximum ${MAX_DOCUMENT_UPLOAD_FILES} files allowed. Please remove some files first.`
          );
          return;
        }

        if (existingFileNames.has(file.name)) {
          toast.warning(`${file.name} is already selected`);
          return;
        }

        nextFiles.push({
          error: getDocumentFileValidationError(file) ?? undefined,
          file,
          id: `${file.name}-${Date.now()}-${Math.random()}`,
        });
      });

      if (nextFiles.length > 0) {
        setSelectedFiles((currentFiles) => [...currentFiles, ...nextFiles]);
      }
    },
    [selectedDataSource, selectedFiles]
  );

  const handleDragEnter = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragCounterRef.current += 1;
    setIsDragOver(true);
  }, []);

  const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  }, []);

  const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      dragCounterRef.current = 0;
      setIsDragOver(false);
      handleFileSelect(event.dataTransfer.files);
    },
    [handleFileSelect]
  );

  const openFilePicker = useCallback(() => {
    if (!selectedDataSource) {
      toast.error("Please select a data source first");
      return;
    }
    fileInputRef.current?.click();
  }, [selectedDataSource]);

  const handleUploadError = useCallback(
    (file: File, id: string, error: UploadError) => {
      const errorMessage = error?.message || "Upload failed";

      setSelectedFiles((currentFiles) =>
        currentFiles.map((selectedFile) =>
          selectedFile.id === id
            ? { ...selectedFile, error: errorMessage }
            : selectedFile
        )
      );

      toast.error(file.name, {
        description: errorMessage,
      });
    },
    []
  );

  const handleUpload = () => {
    if (!selectedDataSource) {
      toast.error("Please select a data source");
      return;
    }

    if (!selectedGroup) {
      toast.error("No default group found for this data source");
      return;
    }

    if (validFiles.length === 0) {
      toast.error("No valid files to upload");
      return;
    }

    setIsUploading(true);

    try {
      const uploadService = BackgroundUploadService.getInstance();
      const activeFileNames = new Set(
        uploadService
          .getAllTasks()
          .filter(
            (task) => task.status === "pending" || task.status === "uploading"
          )
          .map((task) => task.file.name)
      );

      const filesToUpload = validFiles.filter(({ file }) => {
        if (activeFileNames.has(file.name)) {
          toast.warning(`${file.name} is already in the upload queue`);
          return false;
        }

        return true;
      });

      if (filesToUpload.length === 0) {
        toast.warning("All selected files are already being uploaded");
        return;
      }

      filesToUpload.forEach(({ file, id }) => {
        uploadService.addUpload(
          file,
          selectedDataSource,
          selectedGroup,
          `Uploaded ${file.name}`,
          (error) => {
            if (error) {
              handleUploadError(file, id, error);
            }
          }
        );
      });

      toast.success(`${filesToUpload.length} file(s) added to upload queue`);
      setSelectedFiles([]);
      queryClient.invalidateQueries({ queryKey: ["documents-files"] });
      queryClient.invalidateQueries({ queryKey: ["kb-docs"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-base-documents"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-bases"] });
      queryClient.invalidateQueries({ queryKey: ["agent-kb-docs"] });
      queryClient.invalidateQueries({ queryKey: ["command-v2-kb-docs"] });
    } finally {
      setIsUploading(false);
    }
  };

  if (!hasPermission) {
    return (
      <div className="p-5 sm:p-6">
        <AccessDeniedCard message="You don't have permission to view data sources. Contact your administrator if you need access." />
      </div>
    );
  }

  return (
    <div className="w-full space-y-5 bg-muted/20 p-5 sm:p-6">
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Database className="h-4 w-4 text-primary" />
          <Label htmlFor="kb-upload-datasource" className="text-sm font-medium">
            Data source
          </Label>
          <span className="text-sm text-destructive">*</span>
        </div>
        <Select
          value={selectedDataSource}
          onValueChange={setSelectedDataSource}
          disabled={isLoadingDataSources || availableDatasources.length === 0}
        >
          <SelectTrigger id="kb-upload-datasource" className="h-11 w-full bg-background">
            <SelectValue
              placeholder={
                isLoadingDataSources
                  ? "Loading data sources..."
                  : availableDatasources.length === 0
                    ? "No data sources available"
                    : "Select a data source"
              }
            >
              {selectedDataSourceName ? (
                <span className="truncate font-medium">{selectedDataSourceName}</span>
              ) : null}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="max-h-[240px]">
            {availableDatasources.map((datasource) => (
              <SelectItem
                key={datasource.id}
                value={datasource.id}
                textValue={datasource.name}
              >
                <div className="flex flex-col items-start gap-0.5 py-0.5">
                  <span className="font-medium leading-none">{datasource.name}</span>
                  <span className="text-xs text-muted-foreground">
                    Created {new Date(datasource.created_at).toLocaleDateString()}
                  </span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isLoadingDataSources && (
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <RefreshCw className="h-3 w-3 animate-spin" />
            Loading data sources...
          </p>
        )}
        {!isLoadingDataSources && availableDatasources.length === 0 && (
          <p className="text-xs text-muted-foreground">
            {restrictToAttached
              ? "Attach a knowledge base to this agent before uploading documents."
              : "Create a data source first to upload documents."}
          </p>
        )}
        <CreateDataSourceDialog
          trigger={
            <Button
              type="button"
              variant="link"
              className="h-auto justify-start p-0 text-sm font-medium"
            >
              + Create new data source
            </Button>
          }
        />
      </div>

      <div
        role="button"
        tabIndex={0}
        className={cn(
          "flex w-full cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-12 text-center transition-all sm:py-16",
          isDragOver
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/25 bg-muted/10 hover:border-primary/40 hover:bg-muted/30",
          (!selectedDataSource || availableDatasources.length === 0) &&
            "pointer-events-none opacity-60"
        )}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={openFilePicker}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openFilePicker();
          }
        }}
      >
        <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-violet-100 dark:bg-violet-900/30">
          <Upload className="h-7 w-7 text-primary" />
        </span>
        <p className="text-lg font-semibold text-foreground">
          Drop files here or click to browse
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          Up to {MAX_DOCUMENT_UPLOAD_FILES} files · 100MB per file
        </p>
        {selectedDataSourceName && (
          <p className="mt-1 text-xs text-muted-foreground">
            Uploading to{" "}
            <span className="font-medium text-foreground">{selectedDataSourceName}</span>
          </p>
        )}
      </div>

      <FormatSupportChips />

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={getDocumentFileAcceptValue()}
        onChange={(event) => handleFileSelect(event.target.files)}
        className="hidden"
      />

      {selectedFiles.length > 0 && (
        <div className="space-y-3 rounded-xl border bg-background p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">
              Selected files ({selectedFiles.length})
            </h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setSelectedFiles([])}
            >
              Clear all
            </Button>
          </div>
          <div className="space-y-2">
            {selectedFiles.map((selectedFile) => (
              <div
                key={selectedFile.id}
                className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <FileIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">
                      {selectedFile.file.name}
                    </p>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {formatDocumentFileSize(selectedFile.file.size)}
                      </span>
                      <Badge variant="secondary" className="text-[10px]">
                        {getDocumentFileTypeLabel(selectedFile.file)}
                      </Badge>
                    </div>
                    {selectedFile.error && (
                      <p className="mt-1 flex items-center gap-1 text-xs text-destructive">
                        <AlertCircle className="h-3 w-3" />
                        {selectedFile.error}
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() =>
                    setSelectedFiles((currentFiles) =>
                      currentFiles.filter((file) => file.id !== selectedFile.id)
                    )
                  }
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
          {invalidFiles.length > 0 && (
            <p className="text-sm text-muted-foreground">
              {invalidFiles.length} file(s) have errors and will not be uploaded.
            </p>
          )}
        </div>
      )}

      {uploadTasks.length > 0 && (
        <div className="rounded-xl border bg-background p-4">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold">Active Uploads</h3>
            <span className="text-xs font-medium text-muted-foreground">
              {uploadTasks.length} / {MAX_DOCUMENT_UPLOAD_FILES}
            </span>
          </div>
          <div className="mt-3 max-h-44 space-y-2 overflow-y-auto pr-2">
            {uploadTasks.map((task) => (
              <div
                key={task.id}
                className={cn(
                  "rounded-lg border px-3 py-2",
                  task.status === "failed"
                    ? "border-destructive/40 bg-destructive/10"
                    : "border-transparent bg-muted/50"
                )}
              >
                <div className="flex items-center justify-between gap-3">
                  <span
                    className={cn(
                      "truncate text-sm",
                      task.status === "failed" && "text-destructive"
                    )}
                  >
                    {task.file.name}
                  </span>
                  <span
                    className={cn(
                      "text-xs font-medium text-muted-foreground",
                      task.status === "failed" && "text-destructive"
                    )}
                  >
                    {task.status === "failed"
                      ? "Failed"
                      : `${Math.round(task.progress)}%`}
                  </span>
                </div>
                {task.status === "failed" && task.error && (
                  <p className="mt-1 flex items-center gap-1 text-xs text-destructive">
                    <AlertCircle className="h-3 w-3 shrink-0" />
                    {task.error}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" disabled={!canUpload} onClick={handleUpload}>
          {isUploading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Upload className="mr-2 h-4 w-4" />
          )}
          Upload {validFiles.length || ""} file
          {validFiles.length === 1 ? "" : "s"}
        </Button>
      </div>
    </div>
  );
}
